import z from "zod";
import { emailRegex } from "../../../../lib/regex";

export const updatePersonSchema = z.object({
    name: z.string()
        .min(1, "Inserire il nome")
        .max(50, "Nome troppo lungo (max 50 caratteri)")
        .refine((val) => {
            if (!val) return false;
            const cleaned = val.trim().replaceAll(/[^\p{L}\p{N}\s\-']/gu, "");
            return cleaned.length > 0;
        }, {
            message: "Nome non valido: sono ammessi solo lettere, numeri, spazi, trattini e apostrofi"
        }),

    surname: z.string()
        .min(1, "Inserire il cognome")
        .max(50, "Cognome troppo lungo (max 50 caratteri)")
        .refine((val) => {
            if (!val) return false;
            const cleaned = val.trim().replaceAll(/[^\p{L}\p{N}\s\-']/gu, "");
            return cleaned.length > 0;
        }, {
            message: "Cognome non valido: sono ammessi solo lettere, numeri, spazi, trattini e apostrofi"
        }),

    phone: z.string()
        .refine((val) => {
            if (!val || val === "") return true;

            if (/[''´`'\u200B\u200C\u200D\uFEFF]/.test(val)) {
                return false;
            }

            let cleaned = val.trim().replaceAll(/[\s\-\.]/g, "");

            if (cleaned.startsWith("+39")) cleaned = cleaned.substring(3);
            else if (cleaned.startsWith("0039")) cleaned = cleaned.substring(4);

            if (!/^\d+$/.test(cleaned)) return false;

            return cleaned.length === 10 && cleaned.startsWith("3");
        }, {
            message: "Il numero selezionato non è un cellulare"
        })
        .optional().nullable(),

    emails: z.array(
        z.string().nullable().refine((val) => {
            if (val === "" || val === null || val === undefined) return true;
            return emailRegex.test(val) && !val.includes(" ");
        }, {
            message: "Email non valida"
        })
    ).optional().nullable(),

    linkedinProfile: z.string()
        .refine((val) => {
            if (!val || val === "") return true;
            try {
                new URL(val);
                return true;
            } catch {
                return false;
            }
        }, {
            message: "URL LinkedIn non valido"
        })
        .optional().nullable(),

    role: z.string().optional().nullable(),

    department: z.string().optional().nullable(),

    privacy: z.boolean().optional().nullable(),

    particularities: z.array(z.string())
        .refine((val) => {
            if (!val || val.length === 0) return true;
            return val.every(p => /^[\p{L}\p{N}]+$/u.test(p));
        }, {
            message: "Particolarità devono essere stringhe alfanumeriche senza spazi"
        })
        .optional().nullable(),
});

export type UpdatePersonFormValues = z.infer<typeof updatePersonSchema>;

export const createAssociateToCompanySchema = (notFound: boolean) =>
    z.object({
        companyId: z.string().optional(),
        companyName: z.string().refine(val => val === '').optional().nullable(),
        companyVat: z.string()
            .refine((val) => {
                if (!val || val === "") return true;
                const cleaned = val.trim().replaceAll(/[\s\-\.]/g, "").toUpperCase();
                return /^(IT\d{11}|FR[A-Z0-9]{2}\d{9}|DE\d{9}|ES[A-Z]\d{7}[A-Z]|\d{11})$/.test(cleaned);
            }, {
                message: "Partita IVA non valida. Formato ammesso: IT/FR/DE/ES + codice"
            })
            .optional(),
        companyPhone: z.array(
            z.string().refine((val) => {
                if (val === "") return true;
                let cleaned = val.trim();
                if (cleaned.startsWith("+39")) cleaned = cleaned.substring(3);
                else if (cleaned.startsWith("0039")) cleaned = cleaned.substring(4);
                cleaned = cleaned.replace(/[\s\-\.\/()\+]/g, "");

                return /^\d+$/.test(cleaned) &&
                    cleaned.length >= 6 &&
                    cleaned.length <= 12 &&
                    (cleaned.startsWith("0") || cleaned === "011111111111");
            }, {
                message: "Telefono fisso non valido: deve iniziare con 0 e avere 6-12 cifre"
            })
        ).optional(),
    }).superRefine((data, ctx) => {
        if (!notFound && (!data.companyId || data.companyId.length === 0 || data.companyId === null)) {
            ctx.addIssue({
                code: "custom",
                message: "Selezionare un'azienda",
                path: ['companyId']
            });
        }
        if (notFound && (!data.companyName || data.companyName.length === 0)) {
            ctx.addIssue({
                code: "custom",
                message: "Inserire il nome dell'azienda",
                path: ['companyName']
            });
        }
    });

export type AssociateToCompanyFormValues = z.infer<ReturnType<typeof createAssociateToCompanySchema>>;